import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getIdentifier } from '@zedoc/questionnaire';
import PreviewTabularData from '../../../components/dialogs/PreviewTabularData';
import Button from '../../../components/Button';
import Cluster from '../../../common/components/primitives/Cluster';
import TranslationsSelect from '../../../components/inputs/TranslationsSelect';
import usePresentableAnswersSheet from '../../../utils/usePresentableAnswersSheet';
import copyResponsesToClipboard from '../../../utils/copyResponsesToClipboard';
import { default as QuestionnaireRecordSelect } from '../../../common/selectors/QuestionnaireRecord';
import { useRum } from '../../../containers/RumContext';
import {
  getActiveAnswersSheet,
  getPreviewPatientResponseDialogVisible,
} from '../selectors';
import { closePreviewPatientResponseDialog } from '../actions';

const PreviewPatientResponse = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const awsRum = useRum();
  const answersSheet = useSelector(getActiveAnswersSheet);
  const open = useSelector(getPreviewPatientResponseDialogVisible);

  const [translationId, setTranslationId] = useState();

  const { meta, responses, ready } = usePresentableAnswersSheet(
    answersSheet && answersSheet._id,
    translationId,
  );

  const questionnaireId = answersSheet && answersSheet.getQuestionnaireId();
  const questionnaireRecord = useSelector(
    QuestionnaireRecordSelect.one().whereIdEquals(
      questionnaireId ? getIdentifier(questionnaireId) : null,
    ),
  );

  const openedAtRef = useRef(null);

  // Only previews that have to load count.
  useEffect(() => {
    openedAtRef.current = open && !ready ? Date.now() : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!ready || openedAtRef.current === null) {
      return;
    }
    awsRum?.recordEvent('com.zedoc.rum.preview_patient_responses', {
      duration: Date.now() - openedAtRef.current,
      questionnaireType: questionnaireRecord?.getQuestionnaireType() ?? null,
    });
    openedAtRef.current = null;
  }, [ready, awsRum, questionnaireRecord]);

  const title = `${t('previewAnswers')}${
    answersSheet ? ` v.${answersSheet.questionnaireVersion}` : ''
  }`;
  const handleCopyResponses = () => copyResponsesToClipboard(meta, responses);
  const onClose = () => dispatch(closePreviewPatientResponseDialog());
  const footer = (
    <Cluster>
      <TranslationsSelect
        answersSheetId={answersSheet?._id}
        answersSheetLanguages={
          answersSheet && answersSheet.language ? [answersSheet.language] : []
        }
        onChange={setTranslationId}
        value={translationId}
      />
      <Button
        type="primary"
        data-testid="button-copy-responses"
        onClick={handleCopyResponses}
      >
        {t('copyResponses')}
      </Button>
    </Cluster>
  );

  return (
    <PreviewTabularData
      dataSource={responses}
      open={open}
      title={title}
      onClose={onClose}
      footer={footer}
    />
  );
};

export default PreviewPatientResponse;
